import {
  type CardSlotBlockedReason,
  type CardSlotUiView,
  cardSlotProgress,
  cardSlotSeconds,
} from '@kenocyber/game-domain';
import { type CardSlot, cardArt, cardDescription } from '@kenocyber/shared';
import { useEffect, useRef } from 'react';
import { CardColourControl } from './CardColourControl';

/**
 * **A barra das três cartas, no centro inferior da caça** (gate V28H.I).
 *
 * ## O que ela é, e o que ela não decide
 *
 * Ela desenha o que `cardSlotUiViews` decidiu e envia uma ordem quando o
 * jogador pede. Não conhece arrefecimento, duração, alvo nem morte: essas são
 * perguntas sobre a simulação, e estão respondidas em `hunt/card-hud.ts`, onde
 * podem ser provadas sem DOM.
 *
 * ## Rato e teclado atravessam a MESMA função
 *
 * `order(slot)`, e mais nenhuma. Dois caminhos seriam duas oportunidades de
 * discordar sobre o que "disponível" quer dizer, e o clique e a tecla acabariam
 * a validar coisas diferentes - que é exatamente o defeito que um contrato de
 * controlos existe para impedir.
 *
 * ## Nada depende só da cor
 *
 * Cada estado tem texto: `PRONTA`, os segundos que faltam, `ATIVA` com o tempo,
 * o motivo do bloqueio, `VAZIO`. A moldura e o anel são reforço, nunca a única
 * informação - um jogador que não distinga as cores lê a barra na mesma.
 */

/** A frase de cada motivo. A razão é do domínio; a frase é daqui. */
const BLOCKED_TEXT: Readonly<Record<CardSlotBlockedReason, string>> = Object.freeze({
  wrong_mode: 'Não funciona na caça',
  not_implemented: 'Ainda não disponível',
  ship_dead: 'Nave abatida',
  no_session: 'Sem caça ativa',
});

/** O rótulo de estado, sempre em texto. */
export function cardSlotStatusText(view: CardSlotUiView): string {
  switch (view.state) {
    case 'empty':
      return 'Vazio';
    case 'available':
      return 'PRONTA';
    case 'active':
      return `ATIVA ${cardSlotSeconds(view)}s`;
    case 'cooldown':
      return `${cardSlotSeconds(view)}s`;
    case 'blocked':
      return view.blockedReason === null ? 'Bloqueada' : BLOCKED_TEXT[view.blockedReason];
  }
}

/** O texto completo do tooltip: nome, descrição e atalho. */
export function cardSlotTooltip(view: CardSlotUiView): string {
  if (view.definitionId === null) {
    return `Espaço ${view.index} vazio — equipe uma carta no inventário. Atalho: ${view.index}`;
  }
  const name = view.displayName ?? view.definitionId;
  const description = cardDescription(view.definitionId);
  const parts = [name];
  if (view.level !== null) {
    parts.push(`Nível ${view.level}`);
  }
  if (description !== null) {
    parts.push(description);
  }
  parts.push(`Atalho: ${view.index}`);
  return parts.join(' — ');
}

export interface CardHudBarProps {
  readonly slots: readonly CardSlotUiView[];
  /**
   * A ÚNICA fronteira de ordem. O rato e o teclado chamam-na igual.
   *
   * Ela é assíncrona e a barra não espera por ela: o estado que a barra desenha
   * vem da instantânea, e a instantânea muda quando a autoridade responder.
   */
  readonly onActivate: (slot: CardSlot) => void;
  /**
   * Solta a carga de um espaço (gate V28H.N).
   *
   * Opcional porque as bancadas e as provas que só medem a barra não precisam
   * de a fornecer - e porque quinze das dezasseis cartas não carregam, portanto
   * uma barra sem ela continua a estar certa para elas.
   */
  readonly onRelease?: (slot: CardSlot) => void;
  /** Base das imagens das cartas. Injetável para as provas não irem à rede. */
  readonly assetBaseUrl?: string;
  /** Falso enquanto a caça não está a decorrer: as teclas ficam mudas. */
  readonly enabled?: boolean;
  /**
   * **Como cada carta se apresenta** (gate V28I.BX.1).
   *
   * `full` é a barra de sempre: arte, nome e o estado em texto, incluindo
   * `PRONTA`. `art` é a do mundo aberto, por decisão do dono: a arte ocupa a
   * célula inteira na proporção dela, não há nome, e o estado pronto é a
   * luminosidade normal da própria carta — sem palavra nenhuma. Os estados que
   * NÃO são "pronta" continuam escritos por cima da arte: os segundos de
   * recarga, `ATIVA`, o motivo de um bloqueio e `Vazio`.
   *
   * O nome, a descrição e o atalho continuam no `title` e no `aria-label` nos
   * dois modos: tirá-los do ecrã não os tira de quem lê a barra por outra via.
   */
  readonly presentation?: 'full' | 'art';
}

/**
 * **A raiz dos assets, e o manifesto diz o resto** (gate V28I.AU).
 *
 * Era `/game-assets/cards` com o nome do ficheiro montado à mão a partir do
 * identificador da carta. Isso funcionava por acaso — o pipeline calha nomear o
 * derivado pelo identificador — e deixava de funcionar no dia em que ele
 * deixasse de o fazer. Agora é o mesmo caminho que o separador das cartas usa:
 * a raiz, mais o `file` que o manifesto declara.
 */
export const CARD_HUD_ASSET_BASE_URL = '/game-assets/';

/**
 * Se um evento de teclado deve ser IGNORADO por vir de um campo editável.
 *
 * Exportada porque é uma regra e não um detalhe: escrever `1` no nome de uma
 * skin não pode ligar uma habilidade, e essa afirmação merece ser provada
 * diretamente em vez de através de um componente inteiro.
 *
 * `isContentEditable` cobre o caso que uma lista de etiquetas não cobre: um
 * `div` editável não é `input`, `textarea` nem `select`, e escreve-se nele
 * exatamente da mesma maneira.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select';
}

/** O espaço que uma tecla nomeia, ou `null` quando ela não nomeia nenhum. */
/**
 * **A arte de uma carta no HUD**, resolvida pelo manifesto (gate V28I.AU).
 *
 * As dezasseis existem desde este gate, e o `onError` fica: uma imagem que não
 * carrega — rede, cache, um derivado por regenerar — mostraria o ícone de
 * imagem partida do browser por cima da arena, e o nome por baixo continua a
 * identificar a carta.
 *
 * O PNG já traz a carcaça e a iluminação da raridade. A interface não desenha
 * moldura nenhuma por cima dele; ela compõe o que a arte NÃO diz — nome, tecla,
 * estado, recarga — e o `data-rarity` do contentor continua a existir para o
 * texto e para a acessibilidade.
 */
function CardHudArt({
  assetBaseUrl,
  definitionId,
}: {
  readonly assetBaseUrl: string;
  readonly definitionId: string;
}) {
  const art = cardArt(definitionId);
  if (art === null) {
    return null;
  }
  return (
    <img
      className="card-hud-art"
      src={`${assetBaseUrl}${art.file}`}
      alt=""
      aria-hidden="true"
      width={art.width}
      height={art.height}
      draggable={false}
      onError={(event) => {
        event.currentTarget.style.visibility = 'hidden';
      }}
    />
  );
}

export function cardSlotForKey(key: string): CardSlot | null {
  switch (key) {
    case '1':
      return 'card_slot_1';
    case '2':
      return 'card_slot_2';
    case '3':
      return 'card_slot_3';
    default:
      return null;
  }
}

export function CardHudBar({
  slots,
  onActivate,
  onRelease,
  assetBaseUrl = CARD_HUD_ASSET_BASE_URL,
  enabled = true,
  presentation = 'full',
}: CardHudBarProps) {
  /*
   * O estado mais recente, sem re-subscrever o teclado a cada quadro.
   *
   * A barra recebe uma lista nova quatro vezes por segundo. Se o ouvinte
   * dependesse dela, ele seria removido e reposto quatro vezes por segundo - e
   * uma tecla carregada exatamente na troca perder-se-ia.
   */
  const latest = useRef({ slots, onActivate, onRelease, enabled });
  latest.current = { slots, onActivate, onRelease, enabled };

  /*
   * **Os espaços que este dedo está a segurar** (gate V28H.N).
   *
   * Um `Set` numa ref e não estado de React: ele muda no `keydown` e no
   * `keyup`, e um render por tecla premida seria um render por tecla premida.
   *
   * Existe para a soltura só ser enviada quando a activação PARTIU daqui. Sem
   * ele, largar a tecla 2 numa caçada em que o jogador nunca a carregou - por
   * exemplo depois de trocar de janela - enviaria uma ordem que o servidor teria
   * de recusar, e um pedido por cada tecla larga é ruído sobre a autoridade.
   */
  const held = useRef(new Set<CardSlot>());

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      /*
       * **A repetição automática é ignorada**, e é uma linha só.
       *
       * Manter `1` carregado emite `keydown` dezenas de vezes por segundo. Sem
       * isto, cada uma seria uma ordem: dezenas de pedidos por segundo, todos
       * menos o primeiro recusados, e o servidor a fazer uma liquidação por
       * cada um.
       */
      if (event.repeat) {
        return;
      }
      /* Um atalho não pode roubar um modificador do sistema nem do browser. */
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }
      const slot = cardSlotForKey(event.key);
      if (slot === null) {
        return;
      }
      const current = latest.current;
      if (!current.enabled) {
        return;
      }
      const view = current.slots.find((entry) => entry.slot === slot);
      if (!view?.actionable) {
        return;
      }
      event.preventDefault();
      held.current.add(slot);
      current.onActivate(slot);
    };
    /**
     * **A tecla que se solta** (gate V28H.N).
     *
     * A Lança Solar carrega enquanto a tecla está premida, e é isto que fecha
     * esse contrato. Para as outras quinze, a ordem chega ao servidor, não
     * encontra carga nenhuma e é recusada sem custo - o que é o mesmo que dizer
     * que segurar uma carta que não carrega não faz diferença nenhuma.
     *
     * Sem `event.repeat` aqui: o browser não repete um `keyup`.
     */
    const onKeyUp = (event: KeyboardEvent): void => {
      const slot = cardSlotForKey(event.key);
      if (slot === null || !held.current.delete(slot)) {
        return;
      }
      const current = latest.current;
      if (!current.enabled) {
        return;
      }
      current.onRelease?.(slot);
    };
    /*
     * **E uma janela que perde o foco larga tudo** - alt-tab, um clique fora,
     * um bloqueio de ecrã. Sem isto, a tecla ficaria "premida" para sempre deste
     * lado, e a carga seguinte nasceria já a pensar que estava a ser segurada.
     */
    const onBlur = (): void => {
      const current = latest.current;
      for (const slot of held.current) {
        if (current.enabled) {
          current.onRelease?.(slot);
        }
      }
      held.current.clear();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return (
    /* `section` com nome acessível: um grupo nomeado sem inventar um `fieldset`, que é de formulários. */
    <section
      className={`card-hud${presentation === 'art' ? ' card-hud--art' : ''}`}
      data-testid="card-hud"
      aria-label="Cartas equipadas"
    >
      {slots.map((view) => {
        const status = cardSlotStatusText(view);
        const tooltip = cardSlotTooltip(view);
        const progress = cardSlotProgress(view);
        /*
         * No modo `art` a carta pronta não escreve nada: a luminosidade normal
         * dela é o estado. Os outros estados continuam escritos.
         */
        const art = presentation === 'art';
        const writesStatus = !art || view.state !== 'available';
        return (
          <button
            type="button"
            key={view.slot}
            className={`card-hud-slot card-hud-${view.state}${art ? ' card-hud-slot--art' : ''}`}
            data-testid={`card-hud-${view.slot}`}
            data-state={view.state}
            title={tooltip}
            aria-label={tooltip}
            disabled={!view.actionable || !enabled}
            /*
             * **Premir e largar, e não um clique** (gate V28H.N).
             *
             * `onClick` dispara quando o botão do rato SOBE, e um botão que só
             * fala no fim não consegue exprimir "estou a segurar". Com
             * `pointerdown` e `pointerup` o rato tem exactamente a mesma
             * expressividade da tecla, que é o que o contrato pede.
             *
             * `onPointerLeave` está aqui pela mesma razão do `blur` do
             * teclado: arrastar para fora do botão nunca mais receberia o
             * `pointerup`, e a carga ficaria presa até se completar sozinha.
             */
            onPointerDown={() => {
              /* A MESMA fronteira da tecla, com a mesma condição. */
              if (view.actionable && enabled) {
                held.current.add(view.slot);
                onActivate(view.slot);
              }
            }}
            onPointerUp={() => {
              if (held.current.delete(view.slot) && enabled) {
                onRelease?.(view.slot);
              }
            }}
            onPointerLeave={() => {
              if (held.current.delete(view.slot) && enabled) {
                onRelease?.(view.slot);
              }
            }}
          >
            <span className="card-hud-key" aria-hidden="true">
              {view.index}
            </span>
            {view.definitionId === null ? (
              <span className="card-hud-empty">—</span>
            ) : (
              <CardHudArt assetBaseUrl={assetBaseUrl} definitionId={view.definitionId} />
            )}
            {art ? null : <span className="card-hud-name">{view.displayName ?? 'Vazio'}</span>}
            {writesStatus ? (
              <span className="card-hud-status" data-testid={`card-hud-status-${view.slot}`}>
                {status}
              </span>
            ) : null}
            {view.state === 'cooldown' || view.state === 'active' ? (
              <span
                className="card-hud-ring"
                data-testid={`card-hud-ring-${view.slot}`}
                aria-hidden="true"
                style={{ ['--card-hud-progress' as string]: String(progress) }}
              />
            ) : null}
          </button>
        );
      })}
      {/*
        A paleta dos efeitos de carta (gate V28H.O).

        Aqui e nao numa pagina de definicoes: a coisa que ela pinta esta a tres
        centimetros dela, e quem quer mudar a cor esta a olhar para o efeito
        quando decide.
      */}
      <CardColourControl />
    </section>
  );
}
